import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Sun, Moon, Menu, X, User, LogOut, Library, Receipt, ShieldCheck } from "lucide-react";
import { useAuth, useCart, useTheme } from "../context/AppContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const LINKS = [
  ["/", "Home", "home"], ["/shop", "Shop", "shop"], ["/gallery", "Gallery", "gallery"],
  ["/videos", "Videos", "videos"], ["/about", "About", "about"], ["/contact", "Contact", "contact"],
];

const linkCls = ({ isActive }) =>
  `text-[0.7rem] uppercase tracking-[0.22em] transition-colors duration-200 ${isActive ? "text-wb-gold" : "text-wb-muted hover:text-wb-text"}`;

function AccountMenu() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  if (!user)
    return (
      <Link to="/login" data-testid="nav-login-link" className="hidden sm:inline text-[0.7rem] uppercase tracking-[0.22em] text-wb-muted hover:text-wb-gold transition-colors">
        Sign in
      </Link>
    );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="nav-account-button" className="p-2 text-wb-muted hover:text-wb-gold transition-colors outline-none">
        <User size={18} strokeWidth={1.5} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-none">
        <DropdownMenuLabel className="font-normal">
          <div className="text-sm" data-testid="nav-account-name">{user.name || user.email}</div>
          <div className="eyebrow mt-1" data-testid="nav-account-role">{user.role_name || user.role}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="nav-library-link" onClick={() => nav("/library")}><Library size={14} className="mr-2" />My Library</DropdownMenuItem>
        <DropdownMenuItem data-testid="nav-orders-link" onClick={() => nav("/orders")}><Receipt size={14} className="mr-2" />Order history</DropdownMenuItem>
        {user.permissions?.length > 0 && (
          <DropdownMenuItem data-testid="nav-admin-link" onClick={() => nav("/admin")}><ShieldCheck size={14} className="mr-2" />Admin dashboard</DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="nav-logout-button" onClick={() => { logout(); nav("/"); }}><LogOut size={14} className="mr-2" />Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const Navbar = () => {
  const { theme, toggle } = useTheme();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b wb-line backdrop-blur-xl" style={{ background: "var(--wb-nav)" }}>
      <div className="wb-container flex h-20 items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-3">
          <span className="font-display text-2xl tracking-[0.18em] text-wb-text">WARNA</span>
          <span className="font-display text-2xl italic text-wb-gold">Borneo</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map(([to, label, id]) => (
            <NavLink key={to} to={to} end={to === "/"} className={linkCls} data-testid={`nav-${id}-link`}>{label}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-4">
          <button onClick={toggle} data-testid="theme-toggle-button" aria-label="Toggle theme" className="p-2 text-wb-muted hover:text-wb-gold transition-colors">
            {theme === "dark" ? <Sun size={18} strokeWidth={1.5} /> : <Moon size={18} strokeWidth={1.5} />}
          </button>
          <AccountMenu />
          <Link to="/cart" data-testid="nav-cart-button" className="relative p-2 text-wb-muted hover:text-wb-gold transition-colors">
            <ShoppingBag size={18} strokeWidth={1.5} />
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-0.5 -right-0.5 grid h-4 min-w-4 place-items-center px-1 text-[0.6rem] font-mono bg-wb-gold text-[color:var(--wb-on-gold)]">{count}</span>
            )}
          </Link>
          <button className="lg:hidden p-2 text-wb-muted" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t wb-line wb-container flex flex-col gap-5 py-6" onClick={() => setOpen(false)}>
          {LINKS.map(([to, label, id]) => (
            <NavLink key={to} to={to} end={to === "/"} className={linkCls} data-testid={`mobile-nav-${id}-link`}>{label}</NavLink>
          ))}
          <NavLink to="/library" className={linkCls} data-testid="mobile-nav-library-link">My Library</NavLink>
          <NavLink to="/login" className={linkCls} data-testid="mobile-nav-login-link">Account</NavLink>
        </nav>
      )}
    </header>
  );
};
